import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "../../../stores/RootStore";

export default class PanelYardManagementStore {
    // Observables
    isLoading = false;
    error: string | null = null;
    yardInventory: any[] = [];
    yardLayout: any = {};
    panelsInYard: any[] = [];
    yardMetrics: any = {};

    // Constructor
    constructor(private rootStore: RootStore) {
        makeAutoObservable(this);
    }

    // Actions
    setLoading(loading: boolean) {
        this.isLoading = loading;
    }

    setError(error: string | null) {
        this.error = error;
    }

    // Async actions
    async loadYardInventory(panelId?: string) {
        try {
            this.setLoading(true);
            this.setError(null);

            // Mock data for now
            const mockYardMetrics = {
                totalPanels: 125,
                readyForShipping: 45,
                inQualityCheck: 12,
                awaitingInspection: 8,
                yardUtilization: 78,
            };

            const mockPanelsInYard = [
                {
                    id: "panel-yard-1",
                    panelNumber: "P-001",
                    jobNumber: "JOB-2024-001",
                    location: "A-1-3",
                    status: "Ready for Shipping",
                    dateReceived: "2024-07-01",
                    lastInspection: "2024-07-05",
                },
                {
                    id: "panel-yard-2",
                    panelNumber: "P-002",
                    jobNumber: "JOB-2024-001",
                    location: "B-2-1",
                    status: "Quality Check",
                    dateReceived: "2024-07-03",
                    lastInspection: null,
                },
            ];

            const mockYardLayout = {
                sections: [
                    { id: "A", name: "Section A", rows: 5, columns: 10 },
                    { id: "B", name: "Section B", rows: 4, columns: 8 },
                ],
                occupancy: {
                    "A": 0.75,
                    "B": 0.60,
                }
            };

            runInAction(() => {
                this.yardMetrics = mockYardMetrics;
                this.panelsInYard = mockPanelsInYard;
                this.yardLayout = mockYardLayout;
                this.yardInventory = mockPanelsInYard;
            });
        } catch (error: any) {
            runInAction(() => {
                this.setError(error.message || "Failed to load yard inventory");
            });
            console.error("Failed to load yard inventory:", error);
        } finally {
            this.setLoading(false);
        }
    }

    async movePanel(panelId: string, newLocation: string) {
        try {
            this.setLoading(true);
            this.setError(null);

            // Mock move operation
            console.log(`Moving panel ${panelId} to ${newLocation}`);

            // Simulate API call delay
            await new Promise(resolve => setTimeout(resolve, 1000));

            runInAction(() => {
                const panel = this.panelsInYard.find(p => p.id === panelId);
                if (panel) {
                    panel.location = newLocation;
                    panel.lastMoved = new Date().toISOString();
                }
            });
        } catch (error: any) {
            runInAction(() => {
                this.setError(error.message || "Failed to move panel");
            });
            console.error("Failed to move panel:", error);
        } finally {
            this.setLoading(false);
        }
    }

    // Computed values
    get totalPanelsInYard() {
        return this.panelsInYard.length;
    }

    get panelsReadyForShipping() {
        return this.panelsInYard.filter(panel => panel.status === "Ready for Shipping");
    }
}
